import {
    useCallback,
    useMemo,
} from 'react'
import moment, {
    Moment,
} from 'moment'
import {
    DatePicker,
    DatePickerSlotProps,
} from '@mui/x-date-pickers/DatePicker'

import Typography from '@mui/material/Typography'
import useStyles from './DateRangeFilterInput.styles'
import {
    DateRangeFilterInputProps,
    FilterTypeForFilterModel,
    FilterTypeForSelector,
} from './DateRangeFilterInput.types'

const DEFAULT_MASK = 'DD.MM.YYYY HH:mm ZZ'
const MIN_DATE = moment('01.01.2013', 'DD.MM.YYYY')

const SLOT_PROPS: DatePickerSlotProps<Moment, false> = {
    textField: {
        variant: 'standard',
        fullWidth: true,
    },
}

function DateRangeFilterInput({
    item,
    applyValue,
    mask = DEFAULT_MASK,
}: Readonly<DateRangeFilterInputProps>) {
    const {
        classes,
    } = useStyles()

    const parseValueToFilterModelFormat = useCallback((
        value: FilterTypeForSelector,
    ): FilterTypeForFilterModel => {
        return {
            from: value?.from ? value.from.format(mask) : undefined,
            to: value?.to ? value.to.format(mask) : undefined,
        }
    }, [mask])

    const parseFilterModelToValue = useCallback((
        filter: FilterTypeForFilterModel,
    ): FilterTypeForSelector => {
        return {
            from: filter?.from ? moment(filter?.from, mask) : null,
            to: filter?.to ? moment(filter?.to, mask) : null,
        }
    }, [mask])

    const localOnChange = useCallback((newValue: FilterTypeForSelector) => {
        applyValue({
            ...item,
            value: parseValueToFilterModelFormat(newValue),
        })
    }, [
        applyValue,
        item,
        parseValueToFilterModelFormat,
    ])

    const value = useMemo(() => {
        return parseFilterModelToValue(item.value)
    }, [
        item.value,
        parseFilterModelToValue,
    ])

    const onChangeFrom = useCallback((newFromValue: Moment) => {
        localOnChange({
            from: newFromValue,
            to: value.to,
        })
    }, [
        localOnChange,
        value.to,
    ])

    const onChangeTo = useCallback((newToValue: Moment) => {
        const endOfDayValue = newToValue ? newToValue.clone().set({
            hour: 23, minute: 59,
        }) : newToValue

        localOnChange({
            from: value.from,
            to: endOfDayValue,
        })
    }, [
        localOnChange,
        value.from,
    ])

    return (
        <div className={classes.wrapper}>
            <div className={classes.inputWrapper}>
                <Typography className={classes.label}>From</Typography>
                <DatePicker
                    value={value.from}
                    format="DD.MM.YYYY"
                    onChange={onChangeFrom}
                    slotProps={SLOT_PROPS}
                    minDate={MIN_DATE}
                />
            </div>
            <div className={classes.inputWrapper}>
                <Typography className={classes.label}>To</Typography>
                <DatePicker
                    value={value.to}
                    format="DD.MM.YYYY"
                    onChange={onChangeTo}
                    slotProps={SLOT_PROPS}
                    minDate={MIN_DATE}
                />
            </div>
        </div>
    )
}

export default DateRangeFilterInput
